<template>
  <standard-dialog
    v-model="model"
    :title="$t('add_credit')"
    icon="mdi-credit-card-plus"
    max-width="500"
    :save-text="$t('save')"
    :loading="saving"
    :disabled="!valid"
    @save="submit"
    @close="resetForm"
  >
    <v-form ref="form" v-model="valid">
      <autocomplete
        :label="$t('limit')"
        v-model="selectedLimitId"
        :items="limitOptions"
        item-text="text"
        item-value="value"
        :loading="isLoading"
        clearable
        :hide-details="false"
        :hint="selectedLimitHint"
      />

      <text-field
        :label="$t('amount')"
        v-model="newCredit.amount"
        type="number"
        :rules="[(v) => !!v || $t('required'), (v) => v > 0 || $t('must_be_positive')]"
        required
      />

      <dropdown
        :label="$t('type')"
        v-model="newCredit.type"
        :items="creditTypes"
        item-text="text"
        item-value="value"
        required
      />

      <text-field :label="$t('note')" v-model="newCredit.note" />
    </v-form>
  </standard-dialog>
</template>

<script>
import hasModel from "@/mixins/hasModel";
import hasSnackbar from "@/mixins/hasSnackbar";
import axios from "@/plugins/axios";
import StandardDialog from "@/components/dialogs/StandardDialog.vue";
import limitService from "@/services/limit";
import {
  getLimitTypeLabelKey,
  getTimeLimitLabelKey,
  normalizeLimitData,
} from "@/utils/limitHelpers";

export default {
  name: "AddCreditDialog",
  components: {
    StandardDialog,
  },
  mixins: [hasModel, hasSnackbar],
  props: {
    employeeId: {
      type: [Number, String],
      required: true,
    },
  },
  data() {
    return {
      valid: false,
      saving: false,
      newCredit: {
        amount: null,
        type: "topup",
        note: "",
      },
      selectedLimitId: null,
      isLoading: false,
      limits: [],
    };
  },
  computed: {
    creditTypes() {
      return [
        { text: this.$t("topup"), value: "topup" },
        { text: this.$t("admin_adjust"), value: "admin_adjust" },
        { text: this.$t("promo"), value: "promo" },
      ];
    },
    limitOptions() {
      return this.limits.map((limit) => {
        const typeKey = getLimitTypeLabelKey(limit.params?.limit_definition?.active);
        const timeKey = getTimeLimitLabelKey(limit.params?.timing?.active);
        const details = [this.$t(typeKey), this.$t(timeKey)].filter(Boolean).join(" / ");

        return {
          text: details ? `${limit.name} (${details})` : limit.name,
          value: limit.id,
        };
      });
    },
    selectedLimit() {
      return this.limits.find((limit) => limit.id === this.selectedLimitId) || null;
    },
    selectedLimitHint() {
      if (!this.selectedLimit) return "";

      const hasCompanyId =
        this.selectedLimit.company_id !== null && this.selectedLimit.company_id !== undefined;
      const hasRestaurantId =
        this.selectedLimit.restaurant_id !== null &&
        this.selectedLimit.restaurant_id !== undefined;
      const restaurantName = this.$store.state.restaurant?.name;
      const companyName = this.$store.state.company?.name;

      if (hasCompanyId && hasRestaurantId) {
        return restaurantName
          ? this.$t("limit_credit_scope_combined_named", { restaurant: restaurantName })
          : this.$t("limit_credit_scope_combined");
      }

      if (hasCompanyId && !hasRestaurantId) {
        return companyName
          ? this.$t("limit_credit_scope_company_named", { company: companyName })
          : this.$t("limit_credit_scope_company");
      }

      if (!hasCompanyId && hasRestaurantId) {
        return restaurantName
          ? this.$t("limit_credit_scope_restaurant_named", { restaurant: restaurantName })
          : this.$t("limit_credit_scope_restaurant");
      }

      return "";
    },
  },
  mounted() {
    this.loadLimits();
  },
  methods: {
    async loadLimits() {
      this.isLoading = true;
      console.log(
        "Loading limits for company and restaurant...",
        this.$store.state.company,
        this.$store.state.restaurant
      );
      try {
        const { data } = await limitService.getCompanyLimits({
          company_id: this.$store.state.company?.id,
          restaurant_id: this.$store.state.restaurant?.id,
        });

        // Primeni default vrednosti na svaki limit iz API-ja
        this.limits = (data?.data || []).map((limit) => normalizeLimitData(limit));
      } catch (error) {
        this.showErrorSnackbar(this.$t("error_loading_limits"));
      } finally {
        this.isLoading = false;
      }
    },
    async submit() {
      if (!this.$refs.form.validate()) return;
      this.saving = true;
      try {
        await axios.post("/credits/add", {
          amount: this.newCredit.amount,
          type: this.newCredit.type,
          note: this.newCredit.note,
          user_id: this.employeeId,
        });
        this.showSuccessSnackbar(this.$t("credit_added_successfully"));
        this.$emit("saved");
        this.model = false;
      } catch (err) {
        this.showErrorSnackbar(
          err.response?.data?.message || this.$t("error_adding_credit")
        );
      } finally {
        this.saving = false;
      }
    },
    resetForm() {
      this.newCredit = { amount: null, type: "topup", note: "" };
      this.selectedLimitId = null;
      if (this.$refs.form) {
        this.$refs.form.resetValidation();
      }
    },
  },
};
</script>

<style scoped></style>
